// useUnifiedNodeTabs.js

import { useCallback, useEffect, useMemo, useRef } from 'react';

// Context
import { useCurrentLanguage } from 'context/LanguageContext';
import { useNodeTypes } from 'context/NodeTypesContext';

// Utilities
import { getPluralLabel } from 'utilities/Languages';
import { resolveNodeIcon } from 'utilities/resolveNodeIcon';
import { groupChildNodesByType } from 'node/groups/groupChildNodesByType';
import {
  UNIFIED_TAB_KEYS,
  UNIFIED_TAB_META,
  isRelatedTypeTabKey,
  resolveDefaultUnifiedTabKey,
} from 'node/tabs/unifiedNodeTabKeys';
import { isTypeInAncestryPath } from 'utilities/nodeAncestry';
import {
  expandRelatedTypeOrder,
  listTabHiddenRelatedTypeKeys,
} from 'utilities/nodeTypeRelatedLists';
import useMergedWorkflowTemplates from 'hooks/useMergedWorkflowTemplates';

/**
 * Builds unified node tabs and manages default selection + session persistence.
 *
 * @param {object} params
 * @param {object|null} params.node
 * @param {object[]} params.childNodes
 * @param {object|null} params.nodeType
 * @param {string|null} params.activeTabKey
 * @param {function} params.setActiveTabKey
 * @param {function} [params.onUserTabSelect] - Called for explicit user tab clicks.
 *   When omitted, clicks use setActiveTabKey.
 * @param {boolean} params.isMobile
 * @param {boolean} [params.includeAboutTab] - Show About tab (PeekPanel only).
 * @param {boolean} [params.includeActivityTab] - Show Activity tab (Peek platform chrome).
 * @param {boolean} [params.includeFormsTab] - Show Forms tab (main node surface).
 * @param {boolean} [params.includeFloorPlanTab=true] - Show Floor Plan (NodeWorkspace only; hide in Peek).
 * @param {boolean} [params.forceHighlightTab] - Show Highlight even when nodeType.showHighlight is false.
 * @param {boolean} [params.preferAboutTab] - Prefer About as the default when Highlight is unavailable.
 * @param {number} [params.activityCount=0] - Live Activity story count for the tab label.
 * @param {number} [params.activityCommentCount] - Deprecated alias for activityCount.
 * @param {boolean} [params.areChildNodesReady=true] - Whether related-type tabs can be trusted yet.
 * @param {boolean} [params.isNodeLoading=false] - Wait for live node (missionRuns) before settling.
 * @param {boolean} [params.persistSessionTab=true] - Unused; tab memory is disabled.
 *
 * @returns {{ tabs: object[], visibleTypeKeys: string[], relatedTypeTabKey: string|null, isDefaultTabPending: boolean }}
 */
export default function useUnifiedNodeTabs({
  node,
  childNodes = [],
  nodeType,
  activeTabKey,
  setActiveTabKey,
  onUserTabSelect = null,
  isMobile,
  includeAboutTab = false,
  includeActivityTab = false,
  includeFormsTab = true,
  includeFloorPlanTab = true,
  forceHighlightTab = false,
  preferAboutTab = false,
  activityCount = 0,
  activityCommentCount = null,
  areChildNodesReady = true,
  isNodeLoading = false,
  persistSessionTab: _persistSessionTab = true,
}) {
  const { getType } = useNodeTypes();
  const language = useCurrentLanguage();
  const hasInitializedRef = useRef(false);
  const previousNodeKeyRef = useRef(node?.key || null);
  const userSelectedTabRef = useRef(false);

  const selectTab = useCallback((tabKey) => {
    hasInitializedRef.current = true;
    userSelectedTabRef.current = true;

    if (typeof onUserTabSelect === 'function') {
      onUserTabSelect(tabKey);
      return;
    }
    setActiveTabKey?.(tabKey);
  }, [onUserTabSelect, setActiveTabKey]);

  const typeOrder = useMemo(
    () => expandRelatedTypeOrder(nodeType?.suggestedChildren),
    [nodeType?.suggestedChildren],
  );

  // N1 related types with showInTab: false (e.g. PROJECT → TASK).
  const tabHiddenTypeKeys = useMemo(
    () => new Set(listTabHiddenRelatedTypeKeys(nodeType?.suggestedChildren)),
    [nodeType?.suggestedChildren],
  );

  const groupedByType = useMemo(() => {
    return groupChildNodesByType({
      childNodes,
      typeOrder,
      showOnlyProfileRoot: false,
    });
  }, [childNodes, typeOrder]);

  const visibleTypeKeys = useMemo(() => {
    return groupedByType.orderedKeys.filter((typeKeyValue) => {
      // Edge flag: hide from related tabs / hamburger even when children exist.
      if (tabHiddenTypeKeys.has(typeKeyValue)) return false;
      // Structural parents already appear in ancestry breadcrumbs — hide the
      // related tab (it would only list that exclusive parent).
      if (isTypeInAncestryPath(typeKeyValue, node?.ancestryPath)) return false;
      const items = groupedByType.map.get(typeKeyValue) || [];
      return items.length > 0;
    });
  }, [groupedByType, node?.ancestryPath, tabHiddenTypeKeys]);

  const typeDefs = useMemo(() => {
    const out = {};
    visibleTypeKeys.forEach((typeKeyValue) => {
      out[typeKeyValue] = getType(typeKeyValue);
    });
    return out;
  }, [getType, visibleTypeKeys]);

  const getLabelPluralForType = useCallback(
    (def, typeKeyValue) => getPluralLabel(def, typeKeyValue, language),
    [language],
  );

  const showHighlightTab = Boolean(nodeType?.showHighlight) || Boolean(forceHighlightTab);

  const nodeBaseKey = typeof node?.baseKey === 'string' ? node.baseKey.trim() : '';
  const nodeTypeKey = typeof node?.type === 'string' ? node.type.trim() : '';
  const { templates: mergedWorkflowTemplates, isReady: workflowTemplatesReady } = useMergedWorkflowTemplates({
    typeDef: nodeType,
    baseKey: nodeBaseKey,
    typeKey: nodeTypeKey || nodeType?.key || '',
  });

  const showWorkflowTab = workflowTemplatesReady && mergedWorkflowTemplates.length > 0;
  // Default to Playbooks whenever the type offers templates (parallel runs or not).
  const preferWorkflowTab = showWorkflowTab;
  const showFloorPlanTab = Boolean(includeFloorPlanTab)
    && String(node?.type || '').trim().toUpperCase() === 'PROJECT';

  const availableTabKeys = useMemo(() => {
    const keys = [];

    // Settings (About) first in the hamburger; default-tab rules are unchanged.
    if (includeAboutTab) {
      keys.push(UNIFIED_TAB_KEYS.ABOUT);
    }

    if (showHighlightTab) {
      keys.push(UNIFIED_TAB_KEYS.HIGHLIGHT);
    }

    if (showWorkflowTab) {
      keys.push(UNIFIED_TAB_KEYS.WORKFLOW);
    }

    if (showFloorPlanTab) {
      keys.push(UNIFIED_TAB_KEYS.FLOOR_PLAN);
    }

    if (includeActivityTab) {
      keys.push(UNIFIED_TAB_KEYS.ACTIVITY);
    }

    if (includeFormsTab) {
      keys.push(UNIFIED_TAB_KEYS.FORMS);
    }

    keys.push(...visibleTypeKeys);

    return keys;
  }, [
    forceHighlightTab,
    includeAboutTab,
    includeActivityTab,
    includeFloorPlanTab,
    includeFormsTab,
    nodeType?.showHighlight,
    showFloorPlanTab,
    showHighlightTab,
    showWorkflowTab,
    visibleTypeKeys,
  ]);

  const recordIconKey = useMemo(() => {
    return resolveNodeIcon({ node, typeDef: nodeType }).iconKey;
  }, [node, nodeType]);

  const tabs = useMemo(() => {
    const nextTabs = [];

    // Settings (About) first in the hamburger; default-tab rules are unchanged.
    if (includeAboutTab) {
      const aboutMeta = UNIFIED_TAB_META[UNIFIED_TAB_KEYS.ABOUT];
      nextTabs.push({
        key: UNIFIED_TAB_KEYS.ABOUT,
        label: 'Settings',
        iconKey: aboutMeta.iconKey,
        description: aboutMeta.description,
        onClick: () => selectTab(UNIFIED_TAB_KEYS.ABOUT),
      });
    }

    if (showHighlightTab) {
      nextTabs.push({
        key: UNIFIED_TAB_KEYS.HIGHLIGHT,
        label: nodeType?.label || node?.type || 'Highlight',
        iconKey: recordIconKey || String(nodeType?.iconKey || '').trim() || 'star',
        description: String(nodeType?.description || '').trim() || `Focused view of this ${String(nodeType?.label || 'item').toLowerCase()}.`,
        onClick: () => selectTab(UNIFIED_TAB_KEYS.HIGHLIGHT),
      });
    }

    if (showWorkflowTab) {
      const workflowMeta = UNIFIED_TAB_META[UNIFIED_TAB_KEYS.WORKFLOW];
      nextTabs.push({
        key: UNIFIED_TAB_KEYS.WORKFLOW,
        label: 'Playbooks',
        iconKey: workflowMeta.iconKey,
        description: workflowMeta.description,
        onClick: () => selectTab(UNIFIED_TAB_KEYS.WORKFLOW),
      });
    }

    if (showFloorPlanTab) {
      const floorPlanMeta = UNIFIED_TAB_META[UNIFIED_TAB_KEYS.FLOOR_PLAN];
      nextTabs.push({
        key: UNIFIED_TAB_KEYS.FLOOR_PLAN,
        label: 'Floor Plan',
        iconKey: floorPlanMeta.iconKey,
        description: floorPlanMeta.description,
        onClick: () => selectTab(UNIFIED_TAB_KEYS.FLOOR_PLAN),
      });
    }

    if (includeActivityTab) {
      const activityMeta = UNIFIED_TAB_META[UNIFIED_TAB_KEYS.ACTIVITY];
      const resolvedCount = activityCommentCount == null ? activityCount : activityCommentCount;
      const safeCount = Number.isFinite(resolvedCount)
        ? Math.max(0, Math.floor(resolvedCount))
        : 0;
      nextTabs.push({
        key: UNIFIED_TAB_KEYS.ACTIVITY,
        //label: safeCount > 0 ? `Activity (${safeCount > 99 ? '99+' : safeCount})` : 'Activity',
        label: 'Activity',
        iconKey: activityMeta.iconKey,
        description: activityMeta.description,
        onClick: () => selectTab(UNIFIED_TAB_KEYS.ACTIVITY),
      });
    }

    if (includeFormsTab) {
      const formsMeta = UNIFIED_TAB_META[UNIFIED_TAB_KEYS.FORMS];
      nextTabs.push({
        key: UNIFIED_TAB_KEYS.FORMS,
        // Peek has Settings + Forms; Node historically labeled this "Details".
        label: includeAboutTab ? 'Forms' : 'Details',
        iconKey: formsMeta.iconKey,
        description: formsMeta.description,
        onClick: () => selectTab(UNIFIED_TAB_KEYS.FORMS),
      });
    }

    visibleTypeKeys.forEach((typeKeyValue) => {
      const def = typeDefs[typeKeyValue];
      const label = getLabelPluralForType(def, typeKeyValue);
      const description = String(def?.description || '').trim() || `Related ${label.toLowerCase()} attached to this record.`;

      nextTabs.push({
        key: typeKeyValue,
        label,
        iconKey: String(def?.iconKey || '').trim() || 'deployed_code',
        iconResource: undefined,
        description,
        onClick: () => selectTab(typeKeyValue),
      });
    });

    return nextTabs;
  }, [
    activityCount,
    activityCommentCount,
    getLabelPluralForType,
    includeAboutTab,
    includeActivityTab,
    includeFormsTab,
    recordIconKey,
    node?.type,
    nodeType?.description,
    nodeType?.iconKey,
    nodeType?.label,
    selectTab,
    showFloorPlanTab,
    showHighlightTab,
    showWorkflowTab,
    typeDefs,
    visibleTypeKeys,
  ]);

  useEffect(() => {
    const nextNodeKey = node?.key || null;

    if (previousNodeKeyRef.current === nextNodeKey) return;

    const hadPreviousKey = Boolean(previousNodeKeyRef.current);
    previousNodeKeyRef.current = nextNodeKey;
    hasInitializedRef.current = false;
    userSelectedTabRef.current = false;

    if (hadPreviousKey && typeof setActiveTabKey === 'function') {
      setActiveTabKey(null);
    }
  }, [node?.key, setActiveTabKey]);

  // True while we intentionally withhold a default tab (blank > Settings→Workflow flash).
  const isDefaultTabPending = Boolean(isNodeLoading || !workflowTemplatesReady);

  useEffect(() => {
    if (typeof setActiveTabKey !== 'function') return;

    const normalizedActiveTabKey = typeof activeTabKey === 'string' ? activeTabKey.trim() : '';

    // Hold all pane content until live node + workflow templates are known.
    // Prefer a blank wait over Settings → Workflow flash.
    if (isNodeLoading || !workflowTemplatesReady) {
      if (
        !userSelectedTabRef.current
        && normalizedActiveTabKey
        && normalizedActiveTabKey !== UNIFIED_TAB_KEYS.HIGHLIGHT
      ) {
        setActiveTabKey(null);
      }
      return;
    }

    const activeIsValid = Boolean(
      normalizedActiveTabKey && availableTabKeys.includes(normalizedActiveTabKey),
    );

    // User picked a tab this visit — only nudge if that tab disappeared.
    if (userSelectedTabRef.current && activeIsValid) {
      hasInitializedRef.current = true;
      return;
    }

    // Type has playbooks → Playbooks tab on first view.
    if (!userSelectedTabRef.current && preferWorkflowTab) {
      if (normalizedActiveTabKey !== UNIFIED_TAB_KEYS.WORKFLOW) {
        setActiveTabKey(UNIFIED_TAB_KEYS.WORKFLOW);
      }
      hasInitializedRef.current = true;
      return;
    }

    // Keep an explicit related selection while its tab finishes appearing.
    if (
      normalizedActiveTabKey
      && isRelatedTypeTabKey(normalizedActiveTabKey)
      && !areChildNodesReady
    ) {
      return;
    }

    // Already settled on a still-valid tab for this visit.
    if (hasInitializedRef.current && activeIsValid) {
      return;
    }

    const defaultTabKey = resolveDefaultUnifiedTabKey({
      nodeType: showHighlightTab
        ? { ...(nodeType || {}), showHighlight: true }
        : nodeType,
      visibleTypeKeys,
      sessionTabKey: null,
      includeAboutTab,
      includeActivityTab,
      includeFormsTab,
      includeWorkflowTab: showWorkflowTab,
      preferWorkflowTab,
      preferAboutTab,
    });

    const normalizedDefaultTabKey = typeof defaultTabKey === 'string'
      ? defaultTabKey.trim()
      : '';

    if (normalizedActiveTabKey !== normalizedDefaultTabKey) {
      setActiveTabKey(normalizedDefaultTabKey || null);
    }
    hasInitializedRef.current = true;
  }, [
    activeTabKey,
    areChildNodesReady,
    availableTabKeys,
    includeAboutTab,
    includeActivityTab,
    includeFormsTab,
    isNodeLoading,
    node,
    nodeType,
    preferAboutTab,
    preferWorkflowTab,
    setActiveTabKey,
    showHighlightTab,
    showWorkflowTab,
    visibleTypeKeys,
    workflowTemplatesReady,
  ]);

  const relatedTypeTabKey = isRelatedTypeTabKey(activeTabKey) ? activeTabKey : null;

  return {
    tabs,
    visibleTypeKeys,
    relatedTypeTabKey,
    availableTabKeys,
    isDefaultTabPending,
  };
}
